import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  confirm,
  Separator,
  Skeleton,
  Textarea,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Check, ExternalLink, Paperclip, X } from "lucide-react";
import { type ChangeEvent, useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { config } from "@/shared/lib/config";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  useApproveLeaveLineMutation,
  useLineManagerLeaveRequestQuery,
  useRejectLeaveLineMutation,
} from "../api/queries";
import {
  LeaveApproveAdjustFields,
  leaveApproveValuesFromRequest,
  type LeaveApproveAdjustValues,
} from "../ui/LeaveApproveAdjustFields";

const STATUS_LABELS: Record<string, string> = {
  pending_line: "ລໍຖ້າຫົວໜ້າສາຍ",
  pending_hr: "ລໍຖ້າ HR",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

function statusBadgeVariant(
  status: string,
): "default" | "destructive" | "secondary" {
  if (status === "approved") return "default";
  if (status === "rejected") return "destructive";
  return "secondary";
}

export function LeaveLineManagerDetailPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const requestId = id ?? "";

  const query = useLineManagerLeaveRequestQuery(requestId, Boolean(requestId));
  const { data, isLoading, isError } = query;
  const approveMu = useApproveLeaveLineMutation();
  const rejectMu = useRejectLeaveLineMutation();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approveAdjust, setApproveAdjust] =
    useState<LeaveApproveAdjustValues | null>(null);

  useEffect(() => {
    if (data?.canApproveLine) {
      setApproveAdjust(leaveApproveValuesFromRequest(data));
    } else {
      setApproveAdjust(null);
    }
  }, [data]);

  const handleApprove = async () => {
    if (!requestId || !approveAdjust) return;
    const ok = await confirm({
      title: "ອະນຸມັດຂັ້ນສາຍ?",
      description: "ຄຳຂໍຈະຖືກສົ່ງຕໍ່ໃຫ້ HR ພິຈາລະນາຂັ້ນສຸດທ້າຍ.",
      actionText: "ອະນຸມັດ",
    });
    if (!ok) return;
    try {
      await approveMu.mutateAsync({
        id: requestId,
        startDate: approveAdjust.startDate,
        endDate: approveAdjust.endDate,
        days: approveAdjust.days,
      });
      toast.success("ອະນຸມັດຂັ້ນສາຍສຳເລັດ");
      navigate({ to: "/app/hr/leave/line-manager" });
    } catch {
      /* toast ຜິດພາດຈາກ mutation onError */
    }
  };

  const handleReject = async () => {
    if (!requestId) return;
    try {
      await rejectMu.mutateAsync({
        id: requestId,
        rejectionReason: rejectReason.trim() || null,
      });
      toast.success("ປະຕິເສດຂັ້ນສາຍສຳເລັດ");
      setRejectOpen(false);
      navigate({ to: "/app/hr/leave/line-manager" });
    } catch {
      /* toast from mutation */
    }
  };

  if (!requestId) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ບໍ່ມີໄອດີຄຳຂໍ.</p>
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={() => navigate({ to: "/app/hr/leave/line-manager" })}
          >
            ກັບລາຍການ
          </Button>
        </Main>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <Skeleton className="mb-6 h-9 w-full max-w-md" />
          <Skeleton className="h-64 w-full max-w-2xl rounded-xl" />
        </Main>
      </>
    );
  }

  if (isError || !data) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ບໍ່ພົບຄຳຂໍ ຫຼື ບໍ່ອະນຸຍາດເບິ່ງລາຍການນີ້.</p>
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={() => navigate({ to: "/app/hr/leave/line-manager" })}
          >
            ກັບລາຍການ
          </Button>
        </Main>
      </>
    );
  }

  const d = data;
  const dateRangeLabel = `${formatDateLocal(d.startDate)} — ${formatDateLocal(d.endDate)}`;

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">ຄຳຂໍລາ (ທີມ)</h1>
            <p className="mt-1 text-muted-foreground">
              <span className="font-medium text-foreground">
                {d.employeeName}
              </span>
              <span className="mx-1.5 text-border">·</span>
              {d.leaveTypeName}
              <span className="mx-1.5 text-border">·</span>
              {dateRangeLabel}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate({ to: "/app/hr/leave/line-manager" })}
            >
              <ArrowLeft className="size-4" />
              ກັບລາຍການ
            </Button>
            {d.canApproveLine ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-600/90"
                  disabled={approveMu.isPending || rejectMu.isPending || !approveAdjust}
                  onClick={() => void handleApprove()}
                >
                  <Check className="size-4" />
                  ອະນຸມັດຂັ້ນສາຍ
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="gap-1.5"
                  disabled={approveMu.isPending || rejectMu.isPending}
                  onClick={() => setRejectOpen((v) => !v)}
                >
                  <X className="size-4" />
                  ປະຕິເສດ
                </Button>
              </>
            ) : null}
          </div>
        </div>

        {d.canApproveLine && approveAdjust ? (
          <Card className="mb-6 max-w-2xl border-emerald-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">ປັບວັນລາກ່ອນອະນຸມັດ</CardTitle>
              <CardDescription>
                ປັບວັນທີ ຫຼື ຈຳນວນວັນລາຖ້າຈຳເປັນ ກ່ອນສົ່ງຕໍ່ HR
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LeaveApproveAdjustFields
                values={approveAdjust}
                onChange={setApproveAdjust}
              />
            </CardContent>
          </Card>
        ) : null}

        {rejectOpen && d.canApproveLine ? (
          <Card className="mb-6 max-w-2xl border-destructive/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">ເຫດຜົນປະຕິເສດ (ທາງເລືອກ)</CardTitle>
              <CardDescription>ກຳນົດຂໍ້ຄວາມແຈ້ງພະນັກງານ ຫຼື ປ່ອຍວ່າງໄດ້</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                rows={4}
                value={rejectReason}
                placeholder="ເຫດຜົນ..."
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setRejectReason(e.target.value)
                }
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={rejectMu.isPending}
                  onClick={() => void handleReject()}
                >
                  ຢືນຢັນປະຕິເສດ
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRejectOpen(false)}
                >
                  ຍົກເລີກ
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <div className="mx-auto w-full max-w-2xl">
          <Card className="gap-0 overflow-hidden border-border/80 p-0 shadow-sm">
            <CardHeader className="border-b bg-muted/30 py-4">
              <CardTitle className="font-semibold text-lg">ສະຫຼຸບຄຳຂໍ</CardTitle>
              <CardDescription>ສະຖານະ ແລະ ລາຍລະອຽດການລາ</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={statusBadgeVariant(d.status)}>
                  {STATUS_LABELS[d.status] ?? d.status}
                </Badge>
                <span className="rounded-full border bg-muted/40 px-2.5 py-0.5 text-sm tabular-nums">
                  {d.days} ວັນ
                </span>
              </div>
              <Separator />
              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="font-medium text-muted-foreground text-xs uppercase">
                    ພະນັກງານ
                  </dt>
                  <dd className="font-medium">{d.employeeName}</dd>
                </div>
                <div>
                  <dt className="font-medium text-muted-foreground text-xs uppercase">
                    ປະເພດລາ
                  </dt>
                  <dd className="font-medium">{d.leaveTypeName}</dd>
                </div>
              </dl>
              {d.reason ? (
                <>
                  <Separator />
                  <div>
                    <p className="font-medium text-muted-foreground text-xs uppercase">
                      ເຫດຜົນ
                    </p>
                    <p className="mt-1 whitespace-pre-wrap">{d.reason}</p>
                  </div>
                </>
              ) : null}
              {d.attachmentKey ? (
                <>
                  <Separator />
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    className="gap-2"
                    onClick={() =>
                      window.open(
                        `${config.apiUrl}/files/${d.attachmentKey}`,
                        "_blank",
                      )
                    }
                  >
                    <Paperclip className="size-4" />
                    ເປີດແບບຄັດຕິດ
                    <ExternalLink className="size-3.5 opacity-70" />
                  </Button>
                </>
              ) : null}
              {(d.lineApprovedAt || d.approvedAt) && (
                <>
                  <Separator />
                  <div className="space-y-1 text-muted-foreground text-sm">
                    {d.lineApprovedAt ? (
                      <p>
                        ຫົວໜ້າສາຍອະນຸມັດ: {formatDateTimeLocal(d.lineApprovedAt)}
                        {d.lineApprovedByName
                          ? ` (${d.lineApprovedByName})`
                          : ""}
                      </p>
                    ) : null}
                    {d.status === "approved" && d.approvedAt ? (
                      <p>
                        HR ອະນຸມັດ: {formatDateTimeLocal(d.approvedAt)}
                        {d.approvedByName ? ` (${d.approvedByName})` : ""}
                      </p>
                    ) : null}
                  </div>
                </>
              )}
              {d.status === "rejected" && d.rejectionReason ? (
                <>
                  <Separator />
                  <p className="text-destructive text-sm">
                    {d.rejectionReason}
                  </p>
                </>
              ) : null}
              <Separator />
              <p className="text-center text-muted-foreground text-xs">
                ສ້າງ {formatDateTimeLocal(d.createdAt)}
              </p>
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}
